import { useEffect, useRef, useState } from "react";

import { createTokenGate } from "@mobsuccess-devops/axios-instance";
import { MSAxiosInstance as DashboardDataMSAxiosInstance } from "@mobsuccess-devops/dashboard-data-client/axios-instance";
import { getEndpointServiceBranch } from "@mobsuccess-devops/rdc-client/react/endpoint";
import { setTag } from "@sentry/react";

import type { AxiosInstance } from "axios";
import { DateTime } from "luxon";

import { setupCacheBusting } from "../../public/fetch";

import {
  blockedDomainRequestInterceptor,
  makeBlockedDomainResponseErrorInterceptor,
} from "./blocked-domain-fallback";
import { largePayloadRequestInterceptor } from "./large-payload-interceptor";
import { resolvePhpClientEndpoint } from "./php-client-endpoint";
import { PlatformMSAxiosInstance } from "./platform";

type Interceptor<T extends "request" | "response"> = {
  type: T;
  name: string;
  instances: AxiosInstance[];
  params: { current: Parameters<AxiosInstance["interceptors"][T]["use"]> };
};

type InstanceInfo = {
  base: string;
  instance: AxiosInstance;
  setupDashboardData?: boolean;
};

export type AxiosInstancesConfig =
  | Record<string, InstanceInfo>
  | [Record<string, InstanceInfo>, { cacheBusting?: boolean }];

export class AxiosInstances {
  private requests: Array<Interceptor<"request">> = [];
  private responses: Array<Interceptor<"response">> = [];
  public instances: Record<string, InstanceInfo>;
  // Set by the session provider once it can re-establish a session; until
  // then the gate lets every request through (no refresh possible anyway).
  private bearerRefresher: (() => Promise<string | null>) | null = null;
  private tokenGate = createTokenGate({
    refresh: () => this.bearerRefresher?.() ?? Promise.resolve(null),
  });

  constructor(config: AxiosInstancesConfig) {
    const [instances, { cacheBusting }] = Array.isArray(config)
      ? config
      : [config, {}];
    this.instances = instances;
    for (const [name, { instance, base, setupDashboardData }] of Object.entries(
      instances,
    )) {
      const resolvedBase =
        name === "php" ? resolvePhpClientEndpoint(base) : base;
      if (setupDashboardData) {
        DashboardDataMSAxiosInstance.defaults.baseURL = resolvedBase;
      }
      instance.defaults.baseURL = resolvedBase;
    }
    if (cacheBusting) {
      setupCacheBusting(
        Object.values(instances).map(({ instance }) => instance),
      );
    }
    for (const instance of this.getAllInstances()) {
      // Refreshes an expiring Cognito JWT before the request leaves, so a
      // refocused tab does not fan out a burst of 401s (see token-gate).
      instance.interceptors.request.use(this.tokenGate.request);
      instance.interceptors.request.use(largePayloadRequestInterceptor);
      // Reroute blocked `*.makesuccess.io` API hosts to their execute-api
      // fallback on corporate networks that filter the domain as ad-tech.
      instance.interceptors.request.use(blockedDomainRequestInterceptor);
      instance.interceptors.response.use(
        undefined,
        makeBlockedDomainResponseErrorInterceptor(instance),
      );
    }
    this.authorize = this.authorize.bind(this);
  }

  authorize(bearer: string): void {
    // Stamped on every Sentry event so an error can be read against the
    // token lifetime (`auth.expires_at` earlier than the event timestamp =
    // the request left with a dead token). Cleared on sign-out and on
    // opaque bearers, which carry no client-readable expiry.
    const expiresAt = bearer ? this.tokenGate.getExpiry(bearer) : null;
    setTag(
      "auth.expires_at",
      expiresAt === null
        ? undefined
        : DateTime.fromMillis(expiresAt).toUTC().toISO(),
    );
    setTag("auth.flow", describeAuthFlow(bearer));
    for (const instance of this.getAllInstances()) {
      instance.defaults.headers.common.Authorization = bearer;
    }
  }

  // Registers how the token gate re-establishes a session; returns the
  // unregister function so a provider can clear it on unmount.
  setBearerRefresher(
    refresher: (() => Promise<string | null>) | null,
  ): () => void {
    this.bearerRefresher = refresher;
    return () => {
      if (this.bearerRefresher === refresher) {
        this.bearerRefresher = null;
      }
    };
  }

  loadUrlOverrides(bearer: string | null): () => Promise<null> {
    if (!import.meta.env.VITE_DATA_CONTEXT_SERVICE_RDC) {
      global.console.info(
        "[react-shared]: Could not load url overrides as rdc-microservice base url is missing",
      );
      return () => Promise.resolve(null);
    }
    if (!bearer) {
      global.console.info(
        "[react-shared]: Could not load url overrides as bearer is missing",
      );
      return () => Promise.resolve(null);
    }
    return async () => {
      for (const [service, { instance }] of Object.entries(this.instances)) {
        const override = new URLSearchParams(window.location.search).get(
          `${service}-microservice`,
        );
        try {
          if (!override) {
            continue;
          }
          const { body } = await getEndpointServiceBranch(
            `${service}-microservice`,
            override,
            {
              headers: {
                Authorization: bearer,
              },
            },
          );
          if (body.status !== "ready") {
            global.console.warn(
              `[react-shared]: service ${service} (${override}) is not ready`,
            );
            continue;
          }
          instance.defaults.baseURL = body.url;
        } catch {
          global.console.error(
            `[react-shared]: Could not load url overrides for ${service} (${override})`,
          );
        }
      }
      return null;
    };
  }

  private getAllInstances(): AxiosInstance[] {
    return [
      ...new Set([
        PlatformMSAxiosInstance,
        DashboardDataMSAxiosInstance,
        ...Object.values(this.instances).map(({ instance }) => instance),
      ]),
    ];
  }

  useRequestInterceptor(
    name: string,
    ...params: Parameters<AxiosInstance["interceptors"]["request"]["use"]>
  ): boolean {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [registered, setRegistered] = useState(false);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ref = useRef(params);
    ref.current = params;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (this.requests.some((interceptor) => interceptor.name === name)) {
        throw new Error(`Interceptor with name ${name} already exists`);
      }
      const interceptor: Interceptor<"request"> = {
        type: "request",
        name,
        instances: [],
        params: ref,
      };
      this.requests.push(interceptor);
      const cleanups: Array<() => void> = [];
      for (const instance of this.getAllInstances()) {
        const interceptorId = instance.interceptors.request.use(
          (config) => {
            const [onFulfilled] = ref.current;
            if (!onFulfilled) {
              return config;
            }
            return onFulfilled(config);
          },
          (error) => {
            const [, onRejected] = ref.current;
            if (!onRejected) {
              return Promise.reject(error);
            }
            return onRejected(error);
          },
        );
        interceptor.instances.push(instance);
        cleanups.push(() => {
          instance.interceptors.request.eject(interceptorId);
        });
      }
      setRegistered(true);
      return () => {
        setRegistered(false);
        this.requests = this.requests.filter(
          (interceptor) => interceptor.name !== name,
        );
        for (const cleanup of cleanups) {
          cleanup();
        }
      };
    }, [name]);
    return registered;
  }

  useResponseInterceptor(
    name: string,
    ...params: Parameters<AxiosInstance["interceptors"]["response"]["use"]>
  ): boolean {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [registered, setRegistered] = useState(false);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ref = useRef(params);
    ref.current = params;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (this.responses.some((interceptor) => interceptor.name === name)) {
        throw new Error(`Interceptor with name ${name} already exists`);
      }
      const interceptor: Interceptor<"response"> = {
        type: "response",
        name,
        instances: [],
        params: ref,
      };
      this.responses.push(interceptor);
      const cleanups: Array<() => void> = [];
      for (const instance of this.getAllInstances()) {
        const interceptorId = instance.interceptors.response.use(
          (response) => {
            const [onFulfilled] = ref.current;
            if (!onFulfilled) {
              return response;
            }
            return onFulfilled.call(instance, response);
          },
          (error) => {
            const [, onRejected] = ref.current;
            if (!onRejected) {
              return Promise.reject(error);
            }
            return onRejected.call(instance, error);
          },
        );
        interceptor.instances.push(instance);
        cleanups.push(() => {
          instance.interceptors.response.eject(interceptorId);
        });
      }
      setRegistered(true);
      return () => {
        setRegistered(false);
        this.responses = this.responses.filter(
          (interceptor) => interceptor.name !== name,
        );
        for (const cleanup of cleanups) {
          cleanup();
        }
      };
    }, [name]);
    return registered;
  }
}

// Sentry tag value for the credential currently on the instances. Opaque
// flows (`pat:`, `jwt-as:`) are outside the token gate's reach, so knowing
// the flow is the first thing to check on an auth failure.
function describeAuthFlow(
  bearer: string,
): "jwt" | "pat" | "jwt-as" | "none" | "other" {
  if (!bearer) {
    return "none";
  }
  if (bearer.startsWith("Bearer jwt:")) {
    return "jwt";
  }
  if (bearer.startsWith("Bearer pat:")) {
    return "pat";
  }
  if (bearer.startsWith("Bearer jwt-as:")) {
    return "jwt-as";
  }
  return "other";
}
